import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { categoryAPI } from '../../config/api';
import { getCategoryImage, getCategoryName } from '../../utils/adminFields';
import resolveImageUrl from '../../utils/resolveImageUrl';
import swal from 'sweetalert';
import AdminSidebar from '../../components/AdminSidebar';
import { showAppToast } from '../../utils/notifications';

function AdminCategory({ categories, onUpdate }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortMode, setSortMode] = useState('Name');
  const [loading, setLoading] = useState(false);

  const filteredCategories = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return (categories || [])
      .filter((category) => (
        getCategoryName(category).toLowerCase().includes(term) ||
        category.description?.toLowerCase().includes(term)
      ))
      .sort((first, second) => {
        if (sortMode === 'Newest first') return Number(second.id || 0) - Number(first.id || 0);
        if (sortMode === 'Oldest first') return Number(first.id || 0) - Number(second.id || 0);
        return getCategoryName(first).localeCompare(getCategoryName(second));
      });
  }, [categories, searchTerm, sortMode]);

  const handleDelete = async (id, name) => {
    const result = await swal({
      title: 'Delete Category?',
      text: `Are you sure you want to delete "${name}"? This will not delete vehicles in this category.`,
      icon: 'warning',
      buttons: ['Cancel', 'Delete'],
      dangerMode: true,
    });

    if (!result) return;

    setLoading(true);
    try {
      await categoryAPI.delete(id);
      showAppToast({ message: 'Category deleted successfully', type: 'success', duration: 2400 });
      onUpdate();
    } catch (error) {
      showAppToast({ message: error.response?.data?.message || 'Failed to delete category', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className="admin-content px-4 py-8">
      <div className="mx-auto max-w-7xl">
        <div className="admin-header">
          <div>
            <p className="text-accent">Fleet Taxonomy</p>
            <h1 className="section-title text-4xl text-primary-theme">Manage Categories</h1>
            <p className="text-secondary-theme">Total: {categories?.length || 0} categories</p>
          </div>
          <Link to="/admin/category/add" className="btn-luxury">
            <i className="bi bi-plus-lg" aria-hidden="true" />
            Add Category
          </Link>
        </div>

        <div className="admin-filter-strip">
          <input
            type="search"
            placeholder="Search categories..."
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            className="admin-search"
          />
          <select value={sortMode} onChange={(event) => setSortMode(event.target.value)} aria-label="Sort categories">
            <option>Name</option>
            <option>Newest first</option>
            <option>Oldest first</option>
          </select>
        </div>

        <div className="admin-table-card card-luxury">
          <div className="overflow-x-auto">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Description</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCategories.length ? (
                  filteredCategories.map((category) => {
                    const name = getCategoryName(category);
                    const image = getCategoryImage(category);
                    return (
                      <tr key={category.id}>
                        <td>
                          <div className="admin-category-name-cell">
                            {image && (
                              <img
                                src={resolveImageUrl(image)}
                                alt={name}
                                className="admin-category-thumbnail"
                              />
                            )}
                            <span className="font-semibold text-primary-theme">{name}</span>
                          </div>
                        </td>
                        <td className="text-secondary-theme">{category.description}</td>
                        <td className="text-right">
                          <div className="admin-category-actions" aria-label={`Actions for ${name}`}>
                            <Link
                              to={`/admin/category/${category.id}`}
                              className="admin-icon-action"
                              aria-label={`Edit ${name}`}
                              title={`Edit ${name}`}
                            >
                              <i className="bi bi-pencil-square" aria-hidden="true" />
                            </Link>
                            <button
                              type="button"
                              onClick={() => handleDelete(category.id, name)}
                              disabled={loading}
                              className="admin-icon-action admin-icon-action--danger"
                              aria-label={`Delete ${name}`}
                              title={`Delete ${name}`}
                            >
                              <i className="bi bi-trash3-fill" aria-hidden="true" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="3" className="py-10 text-center text-secondary-theme">
                      {searchTerm ? 'No categories found matching your search' : 'No categories yet. Add your first category.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
    </div>

  );
}

export default AdminCategory;
